import { lazy } from '../../utility/Lazy';
import { createExponentialBackoffWait } from '../../utility/wait';
import { createListenerManager } from './ListenerManager';

class CloseConnectionError extends Error {
    readonly reason: string;

    constructor(reason: string) {
        super(`ws connection was closed unexpectedly due to: ${reason}`);
        this.reason = reason;
    }
}

class WebSocketError extends Error {
    constructor() {
        super('ws connection was broken by some indistinguishable reason');
    }
}

function ensureOpenConnection(ws: WebSocket) {
    return new Promise<WebSocket>((resolve, reject) => {
        (async () => {
            switch (ws.readyState) {
                case WebSocket.OPEN:
                    resolve(ws);
                    break;

                case WebSocket.CONNECTING:
                    ws.onopen = () => {
                        ws.onopen = null;
                        ws.onclose = null;
                        ws.onerror = null;

                        resolve(ws);
                    };
                    ws.onclose = event => {
                        console.warn('ws closed while ensuring open connection');
                        ws.onopen = null;
                        ws.onclose = null;
                        ws.onerror = null;

                        reject(new CloseConnectionError(event.reason));
                    };
                    ws.onerror = () => {
                        console.warn('ws got error while ensuring open connection');
                        ws.onopen = null;
                        ws.onclose = null;
                        ws.onerror = null;

                        // Since WebSocket's onerror does not convey any useful diagnostics by design,
                        // we couldn't distinguish what is the cause of this problem :-<
                        reject(new WebSocketError());
                    };
                    break;

                case WebSocket.CLOSING:
                case WebSocket.CLOSED:
                    reject(new CloseConnectionError('already closed'));
                    break;
            }
        })();
    });
}

async function retryWhileConnect(op: () => Promise<void>, timeoutMillisec = 5000 /* [ms] */) {
    const waitWithBackoff = createExponentialBackoffWait();

    const startAt = Date.now();

    async function attempt() {
        try {
            await op();
        } catch (e) {
            if (e instanceof CloseConnectionError || e instanceof WebSocketError) {
                if (Date.now() - startAt > timeoutMillisec) {
                    throw e;
                }
                await waitWithBackoff();
                await attempt();
            } else {
                throw e;
            }
        }
    }

    await attempt();
}

export class StableWebSocket {
    private readonly connectStream: () => Promise<WebSocket>;
    private _ws = lazy<WebSocket>();
    private active = true;

    onclose: ((ev: CloseEvent) => void) | undefined;
    onmessage: ((ev: MessageEvent) => void) | undefined;
    onerror: ((ev: Event) => void) | undefined;

    constructor(connectStream: () => Promise<WebSocket>) {
        this.connectStream = connectStream;
    }

    private eventListenerManagers = {
        close: createListenerManager<(ev: CloseEvent) => void>(),
        message: createListenerManager<(ev: MessageEvent) => void>(),
        error: createListenerManager<(ev: Event) => void>(),
    };

    addEventListener(event: 'close', listener: (ev: CloseEvent) => void): void;
    addEventListener(event: 'message', listener: (ev: MessageEvent) => void): void;
    addEventListener(event: 'error', listener: (ev: Event) => void): void;
    addEventListener(event: string, listener: ((ev: CloseEvent) => void) | ((ev: MessageEvent) => void) | ((ev: Event) => void)): void {
        switch (event) {
            case 'close':
                this.eventListenerManagers.close.addListener(listener as (ev: CloseEvent) => void);
                break;
            case 'message':
                this.eventListenerManagers.message.addListener(listener as (ev: MessageEvent) => void);
                break;
            case 'error':
                this.eventListenerManagers.error.addListener(listener as (ev: Event) => void);
                break;
        }
    }

    removeEventListener(event: 'close', listener: (ev: CloseEvent) => void): void;
    removeEventListener(event: 'message', listener: (ev: MessageEvent) => void): void;
    removeEventListener(event: 'error', listener: (ev: Event) => void): void;
    removeEventListener(event: string, listener: ((ev: CloseEvent) => void) | ((ev: MessageEvent) => void) | ((ev: Event) => void)): void {
        switch (event) {
            case 'close':
                this.eventListenerManagers.close.removeListener(listener as (ev: CloseEvent) => void);
                break;
            case 'message':
                this.eventListenerManagers.message.removeListener(listener as (ev: MessageEvent) => void);
                break;
            case 'error':
                this.eventListenerManagers.error.removeListener(listener as (ev: Event) => void);
                break;
        }
    }

    async get(): Promise<WebSocket> {
        return await this._ws.get();
    }

    async connect(): Promise<StableWebSocket> {
        await retryWhileConnect(async () => {
            const ws = await this.connectStream();
            await ensureOpenConnection(ws);
            this.replaceWebSocket(ws);
        });
        return this;
    }

    async disconnect(): Promise<void> {
        const ws = this._ws.currentValue;
        if (ws) {
            this.active = false; // disable auto reconnection
            ws.close();
            this._ws.clear();
        }
    }

    async send(data: string): Promise<void> {
        const ws = await this._ws.get();
        ws.send(data);
    }

    private replaceWebSocket(ws: WebSocket) {
        ws.onclose = (event: CloseEvent) => {
            const reason = event.reason != '' ? event.reason : 'unknown reason';
            console.warn(`WebSocket disconnected due to: ${reason}`);

            if (this.active) {
                console.warn('reconnect ws automatically');
                this._ws.clear();
                this.connect(); // intentionally non-awaiting
            }
            this.notifyClose(event);
        };

        ws.onerror = (event: Event) => {
            console.warn('WebSocket got an error', event);
            this.notifyError(event);
        };

        ws.onmessage = msg => {
            this.notifyMessage(msg);
        };

        this._ws.setValue(ws);
    }

    private notifyClose(event: CloseEvent) {
        this.eventListenerManagers.close.forEach(listener => {
            listener(event);
        });
        if (this.onclose) this.onclose(event);
    }

    private notifyMessage(event: MessageEvent) {
        this.eventListenerManagers.message.forEach(listener => {
            listener(event);
        });
        if (this.onmessage) this.onmessage(event);
    }

    private notifyError(event: Event) {
        this.eventListenerManagers.error.forEach(listener => {
            listener(event);
        });
        if (this.onerror) this.onerror(event);
    }
}
